import { createFileRoute } from "@tanstack/react-router";
import {
  ContentPanel,
  ContentPanelBody,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@oms/ui";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { ProviderCard } from "@/components/integrations/provider-card";
import { StoreCard } from "@/components/integrations/store-card";
import { PageShell } from "@/components/pages/page-shell";
import { integrationsApi } from "@/lib/api";
import {
  integrationQueryKeys,
  OAUTH_ERROR_MESSAGES,
} from "@/lib/integrations";
import { useAuthStore } from "@/stores/auth-store";

type IntegrationsSearch = {
  status?: "connected" | "error";
  connectionId?: string;
  code?: string;
};

export const Route = createFileRoute("/app/integrations/")({
  validateSearch: (search: Record<string, unknown>): IntegrationsSearch => ({
    status:
      search.status === "connected" || search.status === "error"
        ? search.status
        : undefined,
    connectionId:
      typeof search.connectionId === "string" ? search.connectionId : undefined,
    code: typeof search.code === "string" ? search.code : undefined,
  }),
  component: IntegrationsPage,
});

function IntegrationsPage() {
  const organization = useAuthStore((s) => s.organization);
  const { status, connectionId, code } = Route.useSearch();
  const navigate = Route.useNavigate();

  const providersQuery = useQuery({
    queryKey: integrationQueryKeys.providers(),
    queryFn: async () => {
      const response = await integrationsApi.listProviders();
      return response.data ?? [];
    },
  });

  const connectionsQuery = useQuery({
    queryKey: integrationQueryKeys.connections(),
    queryFn: async () => {
      const response = await integrationsApi.listConnections();
      return response.data ?? [];
    },
    enabled: Boolean(organization?.id),
  });

  const {
    data: connections = [],
    isLoading: connectionsLoading,
    isError: connectionsError,
    refetch: refetchConnections,
  } = connectionsQuery;

  const [syncPolling, setSyncPolling] = useState(false);

  useEffect(() => {
    if (!syncPolling) {
      return;
    }
    const interval = window.setInterval(() => {
      void refetchConnections();
    }, 2000);
    const stop = window.setTimeout(() => {
      window.clearInterval(interval);
      setSyncPolling(false);
    }, 60_000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(stop);
    };
  }, [syncPolling, refetchConnections]);

  useEffect(() => {
    if (status !== "connected") {
      return;
    }
    void refetchConnections();
    const interval = window.setInterval(() => {
      void refetchConnections();
    }, 3000);
    const stopPolling = window.setTimeout(() => {
      window.clearInterval(interval);
    }, 30_000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(stopPolling);
    };
  }, [status, refetchConnections]);

  useEffect(() => {
    if (!status) {
      return;
    }
    const timer = window.setTimeout(() => {
      void navigate({
        search: { status: undefined, connectionId: undefined, code: undefined },
        replace: true,
      });
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [status, navigate]);

  const providers = providersQuery.data ?? [];

  return (
    <PageShell>
      <PageHeader
        title="Integrations"
        description="Connect commerce providers to sync orders, inventory, and tracking."
      />

      {status === "connected" ? (
        <OAuthBanner
          tone="success"
          title="Store connected"
          description={
            connectionId
              ? "Initial sync is running in the background. Your store will appear below once provisioning completes."
              : "Your provider authorization completed successfully."
          }
        />
      ) : null}

      {status === "error" ? (
        <OAuthBanner
          tone="error"
          title="Connection failed"
          description={
            (code && OAUTH_ERROR_MESSAGES[code]) ||
            "We could not complete the provider authorization."
          }
        />
      ) : null}

      {!organization?.id ? (
        <ContentPanel>
          <ContentPanelBody>
            <EmptyState
              title="No active organization"
              description="Switch to a workspace before connecting a store."
            />
          </ContentPanelBody>
        </ContentPanel>
      ) : null}

      <section className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-text">
              Connected stores
            </h2>
            <p className="text-sm text-text-muted">
              Stores linked to {organization?.name ?? "your workspace"}.
            </p>
          </div>
        </div>

        {connectionsLoading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-56 rounded-2xl" />
            ))}
          </div>
        ) : null}

        {connectionsError ? (
          <ErrorState
            description="Could not load connected stores."
            onRetry={() => void refetchConnections()}
          />
        ) : null}

        {!connectionsLoading &&
        !connectionsError &&
        connections.length === 0 ? (
          <EmptyState
            title="No stores connected yet"
            description="Choose a provider below to authorize your first storefront."
          />
        ) : null}

        {connections.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {connections.map((connection) => (
              <StoreCard
                key={connection.id}
                connection={connection}
                onSyncStarted={() => setSyncPolling(true)}
              />
            ))}
          </div>
        ) : null}
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-text">
            Available providers
          </h2>
          <p className="text-sm text-text-muted">
            Provider capabilities are loaded from the platform — nothing is
            hardcoded in the dashboard.
          </p>
        </div>

        {providersQuery.isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-72 rounded-2xl" />
            ))}
          </div>
        ) : null}

        {providersQuery.isError ? (
          <ErrorState
            description="Could not load available providers."
            onRetry={() => void providersQuery.refetch()}
          />
        ) : null}

        {providers.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {providers.map((provider) => (
              <ProviderCard key={provider.provider} provider={provider} />
            ))}
          </div>
        ) : null}
      </section>
    </PageShell>
  );
}

function OAuthBanner({
  tone,
  title,
  description,
}: {
  tone: "success" | "error";
  title: string;
  description: string;
}) {
  const isSuccess = tone === "success";
  return (
    <div
      className={
        isSuccess
          ? "rounded-2xl border border-success/25 bg-success-surface px-4 py-4 sm:px-5"
          : "rounded-2xl border border-error/25 bg-error-surface px-4 py-4 sm:px-5"
      }
    >
      <div className="flex items-start gap-3">
        {isSuccess ? (
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
        ) : (
          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-error" />
        )}
        <div>
          <p
            className={
              isSuccess
                ? "text-sm font-semibold text-success"
                : "text-sm font-semibold text-error"
            }
          >
            {title}
          </p>
          <p
            className={
              isSuccess
                ? "mt-1 text-sm text-success"
                : "mt-1 text-sm text-error"
            }
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}
